import { VERSION_MISMATCH, CODING_VERSION_MISMATCH, AI_CODING_IN_PROGRESS } from 'constants/VersioningErrorConstants';

/**
 * Extracts the error code from an API error response
 * @param {Error} error - The error object from an API response
 * @returns {string|null} The error code or null if not found
 */
export const getErrorCode = (error) => {
  return error?.response?.data?.errors?.[0]?.code || null;
};

/**
 * Checks if an error is a version mismatch error
 * @param {Error} error - The error object from an API response
 * @returns {boolean} True if the error is a version mismatch
 */
export const isVersionMismatch = (error) => {
  const errorCode = getErrorCode(error);
  return errorCode === 'version_mismatch';
};

/**
 * Checks if an error is a coding version mismatch error
 * @param {Error} error - The error object from an API response
 * @returns {boolean} True if the error is a coding version mismatch
 */
export const isCodingVersionMismatch = (error) => {
  const errorCode = getErrorCode(error);
  return errorCode === 'coding_version_mismatch';
};

/**
 * Checks if an error is any type of version mismatch
 * @param {Error} error - The error object from an API response
 * @returns {boolean} True if the error is any type of version mismatch
 */
export const isAnyVersionMismatch = (error) => {
  return isVersionMismatch(error) || isCodingVersionMismatch(error);
};

/**
 * Maps an error code to the appropriate constant
 * @param {Error} error - The error object from an API response
 * @returns {string|null} The appropriate error constant or 'GENERIC_ERROR'
 */
export const getErrorType = (error) => {
  const errorCode = getErrorCode(error);
  
  if (errorCode === 'version_mismatch') {
    return VERSION_MISMATCH;
  }
  
  if (errorCode === 'coding_version_mismatch') {
    return CODING_VERSION_MISMATCH;
  }

  if (errorCode === 'ai_coding_in_progress') {
    return AI_CODING_IN_PROGRESS;
  }

  return error ? 'GENERIC_ERROR' : null;
};

/**
 * Checks if error is a versioning error (transcript editing)
 * @param {Error} error - The error object from an API response
 * @returns {boolean} True if the error is a version mismatch for transcript editing
 */
export const isVersioningError = (error) => isVersionMismatch(error);

/**
 * Checks if error is a coding versioning error (excerpt creation)
 * @param {Error} error - The error object from an API response
 * @returns {boolean} True if the error is a coding version mismatch for excerpt creation
 */
export const isCodingVersioningError = (error) => isCodingVersionMismatch(error);